import { FC, memo, useCallback, useRef } from 'react';
// mui
import { debounce, SelectChangeEvent } from '@mui/material';
// components
import SelectController, { ISelectController } from 'controllers/select-controller';
// styles
import { styles } from './styles';
import { ILocationSelect } from './types';

const LocationSelect: FC<ILocationSelect> = ({
  isLocationsFetching,
  name,
  nextCursor,
  options,
  selectedOptionName,
  setSelectedOptionName,
  setLocationsParams,
  resetLocationsParams,
  ...props
}) => {
  const isOpenRef = useRef(null);

  const getSelectedOptionLabel = useCallback(
    (
      selectOptions: ISelectController['options'],
      selectedValue?: ISelectController['options'][number] | number,
      title?: string
    ) => {
      if (typeof selectedValue === 'number') {
        const option = selectOptions?.find(({ value }) => value === selectedValue);
        return option?.label ?? null;
      }

      if (!selectedValue && title) {
        return title;
      }

      return null;
    },
    []
  );

  const getMultipleSelectedOptionLabel = useCallback(
    (selectedValues?: unknown) => {
      if (Array.isArray(selectedValues) && selectedValues.length) {
        const labels = options
          .filter(({ value }) => selectedValues.includes(value))
          .map(({ label }) => label);
        return labels.length ? labels.join(', ') : selectedOptionName;
      }

      if (Array.isArray(selectedValues) && !selectedValues.length) {
        return null;
      }

      return selectedOptionName || null;
    },
    [options, selectedOptionName]
  );

  const renderSelectValue = useCallback(
    (selectedValue?: ISelectController['options'][number] | number) => {
      const label = props?.multiple
        ? getMultipleSelectedOptionLabel(selectedValue)
        : getSelectedOptionLabel(options, selectedValue, selectedOptionName);

      return (
        <span
          style={{
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '100%',
          }}
          title={label}
        >
          {label ?? (props.multiple ? 'Select Locations' : 'Select Location')}
        </span>
      );
    },
    [getSelectedOptionLabel, getMultipleSelectedOptionLabel, options, selectedOptionName]
  );

  const onSearchChange = useCallback<(value?: string) => void>(
    debounce((searchValue?: string) => {
      setLocationsParams((prev) => ({
        ...prev,
        cursor: undefined,
        search: searchValue ? searchValue : undefined,
      }));
    }),
    []
  );

  const onIntersection = useCallback(() => {
    if (nextCursor && isOpenRef.current) {
      setLocationsParams((prev) => ({
        ...prev,
        cursor: nextCursor,
      }));
    }
  }, [nextCursor]);

  const onChange = useCallback(
    (e: SelectChangeEvent<number | number[]>) => {
      const value = e.target.value;

      if (props?.multiple) {
        const valueList = Array.isArray(value) ? value : [value];

        const labels = options
          .filter(({ value }) => valueList.includes(value as number))
          .map(({ label }) => label);

        setSelectedOptionName(labels.length ? labels.join(', ') : '', null);
        return;
      }

      const selectedValue = value as number;

      const foundOption = options.find(({ value }) => value === selectedValue);

      if (foundOption) {
        setSelectedOptionName(foundOption.label, foundOption.value);
      }
    },
    [props?.multiple, options, setSelectedOptionName]
  );
  const onOpen = () => {
    isOpenRef.current = true;
  };

  const onClose = () => {
    resetLocationsParams();
    isOpenRef.current = false;
  };

  return (
    <>
      <SelectController
        onOpen={onOpen}
        fullWidth
        isSelect
        withError
        name={name}
        options={options}
        paperStyles={styles.paperStyles}
        withDataFetching={true}
        autoFocus
        isFetching={isLocationsFetching}
        onIntersection={onIntersection}
        onSearchChange={onSearchChange}
        onClose={onClose}
        onChange={onChange}
        renderValue={renderSelectValue}
        {...props}
      />
    </>
  );
};

export default memo(LocationSelect);
